/**
 * Layout des Spielleiter-Dashboards.
 *
 * Das Layout besteht nur aus Daten: Reihenfolge, Breite und Zustand je Modul. Welche Module es
 * gibt und was sie anzeigen, steht woanders (siehe `modules.tsx`). Dadurch lässt sich ein Modul
 * verschieben, einklappen oder später ergänzen, ohne die Spiellogik zu berühren.
 */
export interface DashboardLayout {
  version: number;
  /** Reihenfolge der Module von links oben nach rechts unten. */
  order: string[];
  /** Breite in Spalten (1 bis 3). */
  span: Record<string, number>;
  /** Eingeklappte Module – der Inhalt ist verborgen, die Überschrift bleibt. */
  collapsed: Record<string, boolean>;
  /** Gesperrt heißt: kein Verschieben, kein Ändern der Breite. Einklappen bleibt erlaubt. */
  locked: boolean;
}

export const LAYOUT_VERSION = 3;
export const MAX_SPAN = 3;

const STORAGE_KEY = 'noxbury:dashboard-layout';

/** Standardanordnung: links die Runde, in der Mitte die laufende Phase, rechts die Werkzeuge. */
export function defaultLayout(moduleIds: string[]): DashboardLayout {
  const preferred = [
    'flow',
    'actions',
    'win',
    'roster',
    'setup',
    'queue',
    'vote',
    'election',
    'log',
    'twitch',
    'links',
    'sound',
    'camera',
    'admin',
  ];
  const order = [...preferred.filter((id) => moduleIds.includes(id)), ...moduleIds.filter((id) => !preferred.includes(id))];
  const wide = new Set(['flow', 'actions', 'setup', 'log']);
  return {
    version: LAYOUT_VERSION,
    order,
    span: Object.fromEntries(order.map((id) => [id, wide.has(id) ? 2 : 1])),
    collapsed: {},
    locked: true,
  };
}

/**
 * Bringt ein gespeichertes Layout mit den vorhandenen Modulen zusammen. Neue Module landen am
 * Ende, verschwundene fallen weg – so bleibt ein gespeichertes Layout auch nach einer
 * Erweiterung brauchbar.
 */
export function mergeLayout(stored: DashboardLayout | null, moduleIds: string[]): DashboardLayout {
  const base = defaultLayout(moduleIds);
  if (!stored || stored.version !== LAYOUT_VERSION) return base;

  const known = stored.order.filter((id) => moduleIds.includes(id));
  const added = moduleIds.filter((id) => !known.includes(id));
  const order = [...known, ...added];

  return {
    version: LAYOUT_VERSION,
    order,
    span: Object.fromEntries(
      order.map((id) => [id, clampSpan(stored.span?.[id] ?? base.span[id] ?? 1)]),
    ),
    collapsed: Object.fromEntries(order.map((id) => [id, !!stored.collapsed?.[id]])),
    locked: stored.locked ?? true,
  };
}

export function clampSpan(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SPAN, Math.max(1, Math.round(value)));
}

/** Modul an eine andere Stelle setzen. Außerhalb der Liste kann dabei nichts landen. */
export function moveModule(order: string[], id: string, targetIndex: number): string[] {
  const from = order.indexOf(id);
  if (from < 0) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(Math.min(Math.max(targetIndex, 0), next.length), 0, id);
  return next;
}

export function loadLayout(): DashboardLayout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DashboardLayout) : null;
  } catch {
    return null;
  }
}

export function saveLayout(layout: DashboardLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* privates Fenster: dann gilt das Layout nur für diese Sitzung */
  }
}

export function clearLayout(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignorieren */
  }
}
